// apps/widget/src/components/viewfinder.tsx
//
// The camera, the live quality gate and the shutter — shared by the desktop
// capture screen and the phone reached by QR code, so both guide the visitor
// the same way. The phone used to have its own thinner copy: no lighting/pose/
// position readouts, no countdown, and a viewfinder that collapsed to a strip.
//
// The gate runs live against the preview so the visitor is corrected BEFORE the
// shutter fires. Once the frame has been good for long enough the photo is
// taken on a countdown rather than a tap: holding a pose and finding a button
// at the same time is what produces the blurred, off-centre frames the gate
// then rejects. The manual button stays as the way out if the countdown keeps
// resetting, and any movement that breaks the gate cancels it.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { captureFrame, startCamera, stopCamera, type CameraError } from '../lib/camera';
import { detect, isFaceMeshReady, loadFaceMesh, type FaceGeometry } from '../lib/facemesh';
import { checkFrame, createStabiliser, groupStatus, lightingOk, type QualityCheck } from '../lib/quality';
import { t } from '../lib/i18n';
import { FaceOverlay } from './face-overlay';

/** Long enough to settle, short enough that nobody taps the button first. */
const COUNTDOWN_FROM = 3;

export interface Shot {
  /** The frame as taken — not mirrored, so a mark on the left cheek stays left. */
  canvas: HTMLCanvasElement;
  /** Where the face was in the live preview when the shutter fired. */
  geometry: FaceGeometry | null;
  setProgress: (fraction: number) => void;
  /** Releases the camera once the photo is safely on its way. */
  stopCamera: () => void;
}

interface Props {
  /** The camera starts only once this is true — the phone waits to join first. */
  enabled?: boolean;
  /** Uploads the shot. Errors are the caller's to show. */
  onCapture: (shot: Shot) => Promise<void>;
  /** A secondary action under the shutter. */
  extraAction?: (busy: boolean) => ReactNode;
  /** What to show when the camera cannot start. */
  renderCameraError: (error: CameraError) => ReactNode;
  /** Shown over the frozen photo while it is handled. */
  busyLabel?: string;
}

export function Viewfinder({ enabled = true, onCapture, extraAction, renderCameraError, busyLabel }: Props) {
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const stabiliser = useRef(createStabiliser());
  const frame = useRef(0);

  const [quality, setQuality] = useState<QualityCheck | null>(null);
  const [geometry, setGeometry] = useState<FaceGeometry | null>(null);
  const [ready, setReady] = useState(false);
  const [cameraError, setCameraError] = useState<CameraError | null>(null);
  const [busy, setBusy] = useState(false);
  // False while the face model is still loading: the face checks are shown as
  // "Checking…", not "Not good" — a red signal for something not yet looked
  // at read as a broken scanner.
  const [meshSettled, setMeshSettled] = useState(isFaceMeshReady);
  const [progress, setProgress] = useState(0);
  /** null when not counting; 3 → 1 while the frame stays good. */
  const [countdown, setCountdown] = useState<number | null>(null);

  const tick = useCallback(() => {
    frame.current = requestAnimationFrame(tick);
    const element = video.current;
    if (!element || element.readyState < 2) return;

    const face = detect(element, performance.now());
    const check = checkFrame(element, face);

    setGeometry(face);
    setQuality(check);
    // Requires consecutive passing frames, so the button does not flicker on
    // and off while the visitor breathes.
    setReady(stabiliser.current.push(check.passing));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    // The face model loads alongside the camera, not after it (usually it is
    // already loaded: the screens before this one start it).
    void loadFaceMesh().finally(() => {
      if (!cancelled) setMeshSettled(true);
    });

    void (async () => {
      try {
        // Front camera: this is a selfie flow, and defaulting to the rear
        // camera on a phone is a guaranteed retake.
        const media = await startCamera('user');
        if (cancelled) {
          stopCamera(media);
          return;
        }
        stream.current = media;
        if (video.current) {
          video.current.srcObject = media;
          await video.current.play();
        }
        // Checking starts the moment there is video: lighting needs no model,
        // and the face checks join in as soon as it is ready. Guidance
        // degrades rather than blocking if the model cannot load.
        tick();
      } catch (e) {
        setCameraError(e as CameraError);
      }
    })();

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame.current);
      stopCamera(stream.current);
    };
  }, [enabled, tick]);

  const capture = async (): Promise<void> => {
    if (!video.current || busy) return;
    setBusy(true);
    cancelAnimationFrame(frame.current);

    try {
      const canvas = captureFrame(video.current);
      // Freeze on the frame just taken, so the photo holds still from shutter
      // to result.
      video.current.pause();
      await onCapture({ canvas, geometry, setProgress, stopCamera: () => stopCamera(stream.current) });
    } finally {
      setBusy(false);
    }
  };

  // Starts when the gate goes green, cancels the moment it goes red again, so
  // a visitor who drifts out of frame is never photographed mid-correction.
  useEffect(() => {
    if (!ready || busy) {
      setCountdown(null);
      return;
    }

    setCountdown(COUNTDOWN_FROM);
    let remaining = COUNTDOWN_FROM;

    const id = setInterval(() => {
      remaining -= 1;
      if (remaining > 0) {
        setCountdown(remaining);
        return;
      }
      clearInterval(id);
      setCountdown(null);
      void capture();
    }, 1000);

    return () => clearInterval(id);
    // `capture` is stable for the life of a good frame; re-running on every
    // render would restart the count each animation tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, busy]);

  if (cameraError) return <>{renderCameraError(cameraError)}</>;

  return (
    <div className="gs-capture" data-busy={busy || undefined}>
      <div className="gs-viewport">
        <video
          ref={video}
          playsInline
          muted
          // Mirrored for the preview only — people expect a mirror. The captured
          // canvas is NOT mirrored, so a mark on the left cheek stays left.
          style={{ transform: 'scaleX(-1)' }}
        />
        <FaceOverlay geometry={geometry} passing={quality?.passing ?? false} />

        {/* Three readouts rather than one rotating hint: the visitor can see how
            many problems are left, which is what makes correcting them feel
            finite. Each carries its word, never colour alone. */}
        <div className="gs-qchecks" role="status" aria-live="polite">
          {(
            [
              ['lighting', t('capture.check.lighting')],
              ['pose', t('capture.check.pose')],
              ['position', t('capture.check.position')],
            ] as const
          ).map(([key, label]) => {
            // Before the face model is ready only lighting can be judged;
            // pose and position say so instead of failing.
            const pending = !meshSettled && key !== 'lighting';
            const ok = meshSettled ? groupStatus(quality)[key] : key === 'lighting' && quality !== null && lightingOk(quality);
            return (
              <span key={key} className="gs-qcheck" data-ok={(!pending && ok) || undefined} data-pending={pending || undefined}>
                <b>{label}</b>
                {pending ? t('capture.check.checking') : ok ? t('capture.check.good') : t('capture.check.bad')}
              </span>
            );
          })}
        </div>

        {countdown !== null && !busy && (
          <div className="gs-countdown" role="status" aria-live="polite">
            <span className="gs-countdown__label">{t('capture.countdown')}</span>
            <span className="gs-countdown__value">{countdown}</span>
          </div>
        )}

        {busy && (
          <div className="gs-scanning" role="status" aria-live="polite">
            {/* No sweep while only sending: the scan itself plays elsewhere. */}
            {!busyLabel && <span className="gs-scanning__sweep" aria-hidden="true" />}
            <span className="gs-scanning__label">{busyLabel ?? t('capture.analysing')}</span>
          </div>
        )}

        {busy && progress > 0 && <div className="gs-upload-progress" style={{ width: `${progress * 100}%` }} />}
      </div>

      {/* Floated over the viewfinder rather than stacked beneath it, so the
          camera gets the whole panel instead of sharing it with the controls. */}
      <div className="gs-capture__controls">
        {/* One instruction at a time, phrased as a fix rather than an error. */}
        <p className="gs-hint" role="status" aria-live="polite">
          {quality?.hint ?? t('capture.hint')}
        </p>

        <button
          className="gs-btn gs-btn--primary gs-shutter"
          disabled={!ready || busy}
          onClick={() => void capture()}
          data-ready={ready || undefined}
        >
          {busy ? t('capture.ready') : t('capture.take')}
        </button>

        {extraAction?.(busy)}
      </div>
    </div>
  );
}
